import React, { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import JustificationContainer from '~/components/atom/JustificationContainer';
import type { OptionOf } from '~/components/molecule/Dropdown';
import Dropdown from '~/components/molecule/Dropdown';
import { optionListsState } from '~/components/page/Forms/components/Builder/state/optionList';
import type {
  FormBuilder_OptionList,
  FormBuilder_ScreenNode_Block,
  FormData_Select_Dropdown,
  FormData_Select_MultiButton,
  FormData_Select_Radio,
} from '~/graphql/types';
import generateIdForEntity from '~/util/generateIdForEntity';
import OptionsEditor, { createNewOption } from './components/OptionsEditor';
import BlockLayout from '../BlockLayout';
import LocalesInput from '../LocalesInput';
import { blockLabelMap } from '../../constants';

type Props = {
  onChange: (next: FormBuilder_ScreenNode_Block) => void;
  onDelete: () => void;
  nodeId: string;
  block:
    | FormData_Select_Dropdown
    | FormData_Select_MultiButton
    | FormData_Select_Radio;
};

const NEW_OPTION_PAYLOAD = 'new-options';

const InputWithOptions: React.FCC<Props> = ({
  onChange,
  onDelete,
  nodeId,
  block,
}) => {
  const [optionLists, setOptionLists] = useRecoilState(optionListsState);
  const dropdownOptions: Array<OptionOf<string>> = [
    ...optionLists.map(
      ({ id, name }): OptionOf<string> => ({
        key: id,
        label: name,
        payload: id,
      }),
    ),
    {
      key: 'new-list',
      icon: { name: 'plus' },
      label: 'Nieuwe opties defineren',
      payload: NEW_OPTION_PAYLOAD,
    },
  ];

  const [selectedOptionList, setSelectedOptionList] = useState<string | null>(
    block.options ?? null,
  );

  useEffect(() => {
    if (selectedOptionList) {
      onChange({
        __typename: block.__typename,
        options: selectedOptionList,
        key: block.key,
        label: {
          ...block.label,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptionList]);

  return (
    <BlockLayout
      onDelete={onDelete}
      heading={blockLabelMap[block.__typename]}
      blockKey={block.key}
      nodeId={nodeId}
    >
      <JustificationContainer direction="column" gap="m" width="100%">
        <Dropdown
          selectedOptionIdx={dropdownOptions.findIndex(
            ({ payload }) => payload === selectedOptionList,
          )}
          options={dropdownOptions}
          onChange={({ option }) => {
            if (option.payload === NEW_OPTION_PAYLOAD) {
              const newEmptyList = createEmptyList(
                `Nieuw opties ${optionLists.length + 1}`,
              );
              setOptionLists(prev => [...prev, newEmptyList]);

              return setSelectedOptionList(newEmptyList.id);
            }
            return setSelectedOptionList(option.payload);
          }}
        />
        {selectedOptionList && (
          <>
            <LocalesInput
              block={block}
              onChange={onChange}
              subject="label"
              blockKey={block.key}
              nodeId={nodeId}
            />
            <OptionsEditor
              listId={selectedOptionList}
              blockKey={block.key}
              nodeId={nodeId}
            />
          </>
        )}
      </JustificationContainer>
    </BlockLayout>
  );
};

const createEmptyList = (name: string): FormBuilder_OptionList => {
  const id = generateIdForEntity('FORMBUILDER_OPTION');
  return {
    id,
    name,
    __typename: 'FormBuilder_OptionList',
    options: [
      createNewOption({
        __typename: 'FormBuilder_Label_Intl',
        en: `Option 1`,
        nl: `Option 1`,
      }),
    ],
  };
};

export default InputWithOptions;
